/* eslint-disable react-dom/no-unsafe-iframe-sandbox */
import type { TFunction } from "i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, DatePicker, DialogTitle, Form, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, Heading, Text } from "@sentricmusic/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import short from "short-uuid";
import { toast } from "sonner";
import { z } from "zod";

import {
  useCreateRecording,
  useUpdateRecordingMeta,
} from "@/api/mutations";
import {
  useGetMyProfile,
  useGetMyRecordingsDetailed,
  useGetMySong,
  useGetRecordingMetas,
} from "@/api/queries";
import FormError from "@/components/FormError";
import LoadingSmall from "@/components/LoadingSmall";
import { sanitizeInput, sendEvent, sendProperty } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { SpotifyTrack } from "./hooks/useSpotifyOperations";

import { useSpotifyOperations } from "./hooks/useSpotifyOperations";

function createRecordingFormSchema(t: TFunction) {
  return z.object({
    title: z.string().refine(
      value => value.trim().length > 0,
      String(t("regex:recording-enter-title-regex", "Please enter a title for this recording.")),
    ),
    performingartist: z.string().refine(
      value => value.trim().length > 0,
      String(t("regex:recording-add-performing-artist-regex", "Please enter a performing artist for this recording.")),
    ),
    isrc: z.string().optional().refine((value) => {
      if (value == null || value.trim() === "")
        return true;
      return /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/.test(value);
    }, String(t("regex:recording-incorrect-isrc", "Please enter a valid ISRC code."))),
    recordlabel: z.string().optional(),
    releasedate: z.string().optional(),
  });
}

type RecordingFormValues = z.infer<ReturnType<typeof createRecordingFormSchema>>;

type RecordingFormProps = {
  id: string;
  closeForm: () => void;
  aac?: string | null;
};

function RecordingForm({ id, closeForm, aac = null }: RecordingFormProps) {
  const flags = useFlags();
  const accountFromState = useAppStore(s => s.selectedAccount) as string;
  const selectedAccount = aac != null && aac !== "" ? aac : accountFromState;
  const song = useGetMySong({ aac: selectedAccount, wc: id });
  const recordings = useGetMyRecordingsDetailed({ aac: selectedAccount, wc: id });
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  const [spotifyResult, setSpotifyResult] = useState<SpotifyTrack | null>(null);
  const [spotifyURL, setSpotifyURL] = useState("");
  const [spotifyLinkErrorMessage, setSpotifyLinkErrorMessage] = useState("");

  const [recordingId, setRecordingId] = useState<string | null>(null);
  const recordingMetas = useGetRecordingMetas({ id: recordingId ?? "" });

  const createMutation = useCreateRecording();
  const recordingMetaMutation = useUpdateRecordingMeta();

  const profile = useGetMyProfile();

  const { getSpotifyTrackByUrl, isLoading: spotifyLoading } = useSpotifyOperations();

  const { t } = useTranslation("songs");

  const form = useForm<RecordingFormValues>({
    resolver: zodResolver(createRecordingFormSchema(t)),
    mode: "onChange",
    defaultValues: {
      title: "",
      performingartist: "",
      isrc: "",
      recordlabel: "",
      releasedate: "",
    },
  });

  useEffect(() => {
    if (selectedAccount !== "") {
      void song.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  useEffect(() => {
    if (song.isError || song.data === null) {
      closeForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song.isError, song.data]);

  const addRecordingMeta = (rid: string, title: string): void => {
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    recordingMetaMutation.reset();
    setRecordingId(rid);

    if (song.data == null)
      return;

    const payload = {
      id: rid,
      spotify_id: spotifyResult?.id ?? null,
      thumb_url: spotifyResult?.album.images[0]?.url ?? null,
      song_id: song.data.workcode,
    };

    recordingMetaMutation.mutate(payload, {
      onSuccess: (response) => {
        if (response.failedvalidation === true) {
          setValidationErrorMessage(response.messages?.join(", ") ?? "");
          setPending(false);
        }
        else {
          void song.refetch();
          void recordings.refetch();
          void recordingMetas.refetch();

          toast.success(`${t("songs:recording-added-toast")}: ${title}`);

          sendProperty({
            property: "last_recording_added",
            value: "CURRENT_DATE",
            email: profile.data?.email,
          });
          sendEvent({ property: "pe14489857_recording_added" });

          closeForm();
          setPending(false);
        }
      },
      onError: (error: Error) => {
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  const handleSubmit = (data: RecordingFormValues): void => {
    if (song.data == null)
      return;

    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    createMutation.reset();

    const normalizedReleaseDate = data.releasedate != null && data.releasedate.length === 4
      ? `${data.releasedate}-01-01`
      : data.releasedate;
    const releaseDate = normalizedReleaseDate != null && normalizedReleaseDate !== "" ? normalizedReleaseDate : undefined;

    const payload = {
      artistaccountcode: selectedAccount,
      title: sanitizeInput(data.title),
      performingartist: sanitizeInput(data.performingartist),
      isrc: data.isrc != null && data.isrc.trim() !== "" ? data.isrc : undefined,
      recordlabel: data.recordlabel != null && data.recordlabel.trim() !== "" ? data.recordlabel : undefined,
      // generate out own custom code as UIC not actually used
      // so we can use if for matching front-end data with backend data
      uicbarcode: short.generate().substring(0, 13),
      releasedate: releaseDate,
      workcode: song.data.workcode,
      spotifyidentifier: spotifyResult?.id,
      spotifypopularity: spotifyResult?.popularity,
      spotifypopularitylastupdatedutc: spotifyResult?.id == null
        ? null
        : new Date().toISOString(),
      etag: song.data.etag,
    };

    createMutation.mutate(payload, {
      onSuccess: (result) => {
        if (result.failedvalidation === true) {
          setValidationErrorMessage(result.messages?.join(", ") ?? "");
          setPending(false);
        }
        else {
          const uicbarcode = result.model?.uicbarcode;
          if (uicbarcode == null || uicbarcode === "") {
            setMutationErrorMessage("Recording was created but no barcode was returned.");
            setPending(false);
            return;
          }
          addRecordingMeta(uicbarcode, sanitizeInput(data.title));
        }
      },
      onError: (error: Error) => {
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  const handleChangeSpotifyResult = (track: SpotifyTrack): void => {
    const releaseDate = track.album.release_date != null && track.album.release_date.length === 4
      ? `${track.album.release_date}-01-01`
      : track.album.release_date;

    form.setValue("title", sanitizeInput(track.name));
    form.setValue("performingartist", sanitizeInput(track.artists[0]?.name ?? ""));
    form.setValue("isrc", track.external_ids?.isrc ?? "");
    if (releaseDate != null) {
      form.setValue("releasedate", releaseDate);
    }
    setSpotifyResult(track);
    setSpotifyLinkErrorMessage("");
  };

  const handleSpotifyURLSubmit = async (): Promise<void> => {
    setPending(true);
    setSpotifyLinkErrorMessage("");
    setSpotifyResult(null);

    const track = await getSpotifyTrackByUrl(spotifyURL);

    if (track !== null) {
      handleChangeSpotifyResult(track);
    }
    else {
      setSpotifyLinkErrorMessage(String(t("songs:link-spotify-track-invalid-url-error")));
      setSpotifyResult(null);
    }
    setPending(false);
  };

  const removeSpotify = (): void => {
    form.reset({ title: "", performingartist: "", isrc: "", recordlabel: "", releasedate: "" });
    setSpotifyResult(null);
    setSpotifyURL("");
    setSpotifyLinkErrorMessage("");
  };

  if (song.isSuccess && song.data != null) {
    return (
      <div className={isTunecore ? "text-white" : ""}>
        <DialogTitle asChild>
          <Heading as="h3" className="mb-2">
            {t("songs:add-recording-header")}
            {" "}
            <b>{song.data.title}</b>
          </Heading>
        </DialogTitle>

        {Boolean(flags["pd-spotify-recording-song-details-feature"]) && (
          <div className={`flex flex-col gap-6 p-4 mt-4 shadow-md ${isTunecore ? "text-white rounded-lg bg-layer-inverse" : "bg-layer-raised rounded-lg"}`}>
            {spotifyResult === null
              ? (
                  <>
                    <Heading as="h4" className="text-xl">
                      {t("songs:import-from-spotify-header")}
                    </Heading>
                    <Text className="font-sentric-alt">
                      {t("songs:import-from-spotify-text")}
                      {" "}
                      <a
                        className="underline hover:scale-105"
                        href="https://support.spotify.com/us/artists/article/finding-your-artist-url/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("songs:link-spotify-instructions-link")}
                      </a>
                    </Text>
                    <div className="flex flex-col gap-4">
                      <FormInput
                        type="text"
                        placeholder={String(t("songs:link-spotify-paste-track-url"))}
                        value={spotifyURL}
                        onChange={e => setSpotifyURL(e.target.value)}
                        hasError={spotifyLinkErrorMessage !== ""}
                      />
                      {spotifyLinkErrorMessage !== "" && (
                        <Text className="text-sm font-medium leading-snug text-ink-error">{spotifyLinkErrorMessage}</Text>
                      )}
                      <div className="self-end">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => void handleSpotifyURLSubmit()}
                          disabled={spotifyLoading || pending}
                          label={t("songs:link-spotify-fetch-track")}
                        />
                      </div>
                    </div>
                  </>
                )
              : (
                  <div className="flex flex-col gap-4">
                    <iframe
                      sandbox="allow-scripts allow-same-origin"
                      title="preview"
                      src={`https://open.spotify.com/embed/track/${spotifyResult.id}`}
                      className="w-full lg:w-3/4 rounded-xl"
                      height="80"
                      allow="encrypted-media"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={removeSpotify}
                      label={t("songs:remove-selection")}
                    />
                  </div>
                )}
          </div>
        )}

        <Heading as="h4" className="mt-6 text-xl font-semibold">
          {t("songs:recording-details-subheader")}
        </Heading>

        <Form {...form}>
          <form onSubmit={e => void form.handleSubmit(handleSubmit)(e)} className="space-y-4">
            <div className="mt-4 space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("songs:title-placeholder")}</FormLabel>
                    <FormControl>
                      <FormInput {...field} placeholder={String(t("songs:title-placeholder"))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="performingartist"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("songs:performed-by")}</FormLabel>
                    <FormControl>
                      <FormInput {...field} placeholder={String(t("songs:performed-by"))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isrc"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("songs:isrc-placeholder")}</FormLabel>
                    <FormControl>
                      <FormInput {...field} placeholder={String(t("songs:isrc-placeholder"))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="recordlabel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("songs:record-label-placeholder")}</FormLabel>
                    <FormControl>
                      <FormInput {...field} placeholder={String(t("songs:record-label-placeholder"))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="releasedate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("songs:release-date")}</FormLabel>
                    <FormControl>
                      <DatePicker
                        placeholder="DD/MM/YYYY"
                        value={field.value != null && field.value.trim() !== "" ? new Date(field.value) : undefined}
                        onChange={(date: Date | undefined) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, "0");
                            const day = String(date.getDate()).padStart(2, "0");
                            field.onChange(`${year}-${month}-${day}`);
                          }
                          else {
                            field.onChange("");
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormError mutationError={mutationErrorMessage} validationError={validationErrorMessage} />
            <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
              <Button
                variant={isTunecore ? "secondary" : "primary"}
                id="btnRecordingFormAddRecording"
                type="submit"
                disabled={pending}
                label={pending ? String(t("common:loading", "Loading...")) : String(t("songs:add-recording-button"))}
              />
              <Button
                type="button"
                variant="link"
                onClick={closeForm}
                label={t("songs:cancel-and-close")}
              />
            </div>
          </form>
        </Form>
      </div>
    );
  }
  return <LoadingSmall />;
}
export default RecordingForm;
